<template>
  <div v-if="userLoaded">
    <!-- Logged out -->
    <user-landing-page v-if="!user" />

    <div v-if="user && tasksLoaded">
      <!-- <ExtensionInstallPrompt v-if="!userIsNew" /> -->
      <SubscriptionPrompt v-if="showSubscriptionPrompt" :trialDaysLeft="trialDaysLeft" />

      <top-nav v-if="!userIsNew" />

      <OnboardingCarousel v-if="userIsNew && !tasks.length" class="w-full sm:pt-8" />

      <!-- Review -->
      <div v-if="tasks.length">
        <div v-if="reviewConstructionInProgress">
          <div class="flex w-screen h-screen">
            <div class="px-4 py-4 m-auto">
              <div class="space-y-5">
                <div class="text-center sm:text-lg">
                  <CollectionIcon class="w-20 h-20 p-2 mx-auto mb-5 text-gray-400 border-2 border-gray-400 rounded-full" aria-hidden="true" />

                  <h3 class="font-medium text-md">
                    <p class="text-gray-500 dark:text-gray-300">Your content review for...</p>
                    <span class="text-lg italic text-gray-900 dark:text-white">"{{ tasks[0].source.title || "Unknown Title" }}"</span>
                    <p class="text-gray-500 dark:text-gray-300">...will be ready shortly.</p>
                  </h3>

                  <div class="p-4 mt-10 bg-white rounded-lg dark:bg-gray-900 dark:text-gray-200">
                    <p>Enable notifications for your next content review.</p>
                    <div class="mt-6">
                      <router-link
                        to="/profile"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                      >
                        Enable SMS Notifications
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="sm:pt-8" v-else>
          <!-- Show review cards -->
          <div>
            <div v-if="!reviewComplete">
              <!-- Progress bars -->
              <div v-if="tasks.length > 1" class="z-10 flex items-center w-full max-w-2xl px-4 pt-4 mx-auto space-x-2 sm:px-0 sm:pb-3">
                <template v-for="(task, index) in tasks" :key="index">
                  <div
                    class="h-[4px] rounded sm:h-[2.5px]"
                    :class="{
                      'bg-slate-400 dark:bg-slate-400 ': taskIndex >= index,
                      'bg-slate-300/70 dark:bg-slate-700': taskIndex < index,
                    }"
                    :style="`width:${Math.round(100 / tasks.length)}%`"
                  ></div>
                </template>
              </div>

              <!-- Cards -->
              <template v-for="(task, index) in tasks" :key="index">
                <transition-group
                  name="fade"
                  mode="out-in"
                  enter-from-class="opacity-0"
                  enter-active-class="animate__animated animate__fadeIn"
                  enter-to-class="opacity-100"
                  leave-active-class="animate__animated animate__fadeOut"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                  appear
                >
                  <div id="card-container" v-if="taskIndex == index" class="absolute flex w-full transition-all ease-in-out x-auto border-red-50">
                    <TaskCard v-if="task.type != 'reading'" :type="task.type">
                      <template v-slot:header>
                        <TaskHeader :task="task" />
                      </template>

                      <template v-slot:title>{{ task.title }}</template>

                      <template v-slot:instructions> </template>

                      <template v-slot:task>
                        <multiple-choice v-if="task.type == 'multipleChoice'" :options="task.options" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <true-false v-if="task.type == 'trueFalse'" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <text-short v-if="task.type == 'textShort'" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <multiple-answer v-if="task.type == 'multipleAnswer'" :options="task.options" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <order-sequence v-if="task.type == 'sequence'" :options="task.options" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <fill-the-blanks v-if="task.type == 'fillTheBlanks'" :options="task.options" :solution="task.solution" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />

                        <text-long v-if="task.type == 'activeRecall'" @answer="userSubmittedAnswer($event)" @skip="skipTask($event)" />
                      </template>

                      <template v-slot:correctAnswer v-if="result">
                        <transition
                          v-show="result.correct == false && showNextBtn"
                          name="fade"
                          mode="out-in"
                          enter-active-class="transition-all duration-500 ease-in"
                          enter-from-class="opacity-0"
                          enter-to-class="opacity-100"
                          leave-active-class="transition-all duration-500 ease-out"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0"
                          appear
                        >
                          <task-solution :taskSolution="task.solution" />
                        </transition>
                      </template>

                      <template v-slot:feedback v-if="result && task.type != 'activeRecall' && index % 2 == 0 && index > 0">
                        <transition
                          v-if="result.item !== null && showNextBtn"
                          name="fade"
                          mode="out-in"
                          enter-active-class="transition-all duration-500 ease-in"
                          enter-from-class="opacity-0"
                          enter-to-class="opacity-100"
                          leave-active-class="transition-all duration-500 ease-out"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0"
                          appear
                        >
                          <task-feedback @difficultyFeedback="difficultyFeedback($event)" />
                        </transition>
                      </template>

                      <template v-slot:next v-if="result">
                        <transition
                          v-if="result.item !== null && showNextBtn"
                          name="fade"
                          mode="out-in"
                          enter-active-class="transition-all duration-500 ease-in"
                          enter-from-class="opacity-0"
                          enter-to-class="opacity-100"
                          leave-active-class="transition-all duration-500 ease-out"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0"
                          appear
                        >
                          <task-next-button :cta="'Next'" @click="next($event)" />
                        </transition>
                      </template>
                    </TaskCard>

                    <ReadingCard v-else :content="task.reading">
                      <template v-slot:header>
                        <TaskHeader :task="task" />
                      </template>
                      <template v-slot:title>{{ task.title }}</template>
                      <template v-slot:next>
                        <task-next-button :cta="'Done Reading. Next'" v-on:click="next" />
                      </template>
                    </ReadingCard>
                  </div>
                </transition-group>
              </template>
            </div>

            <ReviewCompleteCard v-else :sources="taskSources" />
          </div>
        </div>
      </div>

      <!-- No review required -->
      <div v-else>
        <UserHomeFallback v-if="!userIsNew" :uid="user.uid" />
      </div>
    </div>
  </div>
</template>

<script>
import TopNav from "../components/nav/TopNav.vue";
import TaskCard from "../components/task/TaskCard.vue";
import TaskHeader from "../components/task/TaskHeader.vue";
import MultipleChoice from "../components/questions/MultipleChoice.vue";
import MultipleAnswer from "../components/questions/MultipleAnswer.vue";
import OrderSequence from "../components/questions/OrderSequence.vue";
import TrueFalse from "../components/questions/TrueFalse.vue";
import TextShort from "../components/questions/TextShort.vue";
import FillTheBlanks from "../components/questions/FillTheBlanks.vue";
import TextLong from "../components/questions/TextLong.vue";
import ReadingCard from "../components/task/TaskCardReading.vue";
import TaskFeedback from "../components/task/TaskFeedback.vue";
import TaskSolution from "../components/task/TaskSolution.vue";
import TaskNextButton from "../components/task/TaskNextButton.vue";
import UserHomeFallback from "../components/UserHomeFallback.vue";
// import ExtensionInstallPrompt from "../components/ExtensionInstallPrompt.vue";
import SubscriptionPrompt from "../components/SubscriptionPrompt.vue";
import ReviewCompleteCard from "../components/ReviewCompleteCard.vue";
import { collection, doc, setDoc, getDocs, Timestamp, where, query, updateDoc, increment, limit, getDoc, documentId, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import UserLandingPage from "../views/UserLandingPage.vue";
import { useRouter, useRoute } from "vue-router";
import utilFunctions from "../composables/util";
import firebaseUser from "../composables/userAuth";
import { ref, watchEffect } from "vue";
import "animate.css";
import { CollectionIcon } from "@heroicons/vue/solid";
import OnboardingCarousel from "../components/OnboardingCarousel.vue";
export default {
  components: {
    OnboardingCarousel,
    TopNav,
    TaskCard,
    MultipleChoice,
    MultipleAnswer,
    OrderSequence,
    TrueFalse,
    TextShort,
    FillTheBlanks,
    TextLong,
    ReadingCard,
    TaskFeedback,
    TaskNextButton,
    TaskSolution,
    UserLandingPage,
    UserHomeFallback,
    TaskHeader,
    // ExtensionInstallPrompt,
    CollectionIcon,
    ReviewCompleteCard,
    SubscriptionPrompt,
  },

  setup() {
    const { user, userLoaded } = firebaseUser();
    const { urlHostname, shuffleArray, daysBetweenDates, minutesBetween } = utilFunctions();
    const router = useRouter();
    const route = useRoute();
    const tasks = ref([]);
    const tasksLoaded = ref(false);
    const taskIndex = ref(0);
    const showNextBtn = ref(false);
    const result = ref(null);
    const review = ref(null);
    const { sameDay, dateBounds } = utilFunctions();
    const taskSources = ref([]);
    const reviewConstructionInProgress = ref(false);
    const reviewComplete = ref(false);
    const userIsNew = ref(false);
    const userData = ref(null);
    const showSubscriptionPrompt = ref(false);
    const trialPeriodDays = 30;
    const trialDaysLeft = ref(trialPeriodDays);

    watchEffect(async () => {
      if (user.value) {
        let reviewQuery;
        if (route.query.review) {
          reviewQuery = query(collection(db, "users", user.value.uid, "reviews"), where(documentId(), "==", route.query.review));
        } else {
          reviewQuery = query(collection(db, "users", user.value.uid, "reviews"), where("due", ">=", dateBounds(new Date()).startUtc), where("due", "<=", dateBounds(new Date()).endUtc), limit(1));
        }

        const reviewDueToday = await getDocs(reviewQuery);

        // Get all the tasks
        if (!reviewDueToday.empty) {
          if (!reviewDueToday.docs[0].data().completedAt) {
            review.value = {
              ...reviewDueToday.docs[0].data(),
              id: reviewDueToday.docs[0].id,
            };

            const [taskIds] = reviewDueToday.docs.map((doc) => doc.data().tasks);
            const shuffledTaskIds = shuffleArray(taskIds);

            // build the tasks array
            await Promise.all(
              shuffledTaskIds.map(async (taskId) => {
                const task = await getDoc(doc(db, "tasks", taskId));
                if (task.exists && task.data()?.user == user.value.uid) {
                  tasks.value.push({ ...task.data(), id: taskId });
                  taskSources.value.push(task.data().source);
                }
              })
            );

            if (review.value.tasks.length == 1) {
              if (tasks.value[0].type == "activeRecall") {
                // user just saved this source and navigates from source to Wisery
                reviewConstructionInProgress.value = true;
              }
            }
          }
        }
        tasksLoaded.value = true;

        onSnapshot(doc(db, "users", user.value.uid), async (doc) => {
          userData.value = doc.data();

          // check if there is at least one channel id that is not null
          // const minOneChannelConnected = Object.values(userData.value.channels).some((channel) => channel.id != null);

          const userAgeInMinutes = minutesBetween(parseInt(user.value.metadata.createdAt) / 1000, new Date().getTime() / 1000);
          if (userAgeInMinutes < 1 && reviewDueToday.empty) {
            userIsNew.value = true;
          } else {
            userIsNew.value = false;
          }

          // evaluate subscription status
          const userAgeInDays = daysBetweenDates(new Date(), new Date(userData.value.createdAt.seconds * 1000));
          const subscriptionQuery = query(collection(db, "users", user.value.uid, "subscriptions"), where("status", "in", ["active"]));
          const subscriptionSnap = await getDocs(subscriptionQuery);
          if (subscriptionSnap.empty && userAgeInDays > trialPeriodDays) {
            router.push("subscription");
          }

          if (!subscriptionSnap.empty && userAgeInDays > trialPeriodDays - 10 && userAgeInDays < trialPeriodDays + 1) {
            showSubscriptionPrompt.value = true;
          }

          trialDaysLeft.value = trialPeriodDays - userAgeInDays < 0 ? 0 : trialPeriodDays - userAgeInDays;
        });
      }
    });

    function difficultyFeedback(value) {
      const difficulty = parseFloat(value);
      result.value.difficulty = difficulty;
    }

    async function next(event) {
      // avoid repeated submissions
      if (event) event.target.disabled = true;

      // update review as "startedAt" after first task
      if (!review.value.startedAt && taskIndex.value == 0) {
        const reviewRef = doc(db, "users", user.value.uid, "reviews", review.value.id);
        await updateDoc(reviewRef, { startedAt: Timestamp.now() });
      }

      // Manually set result for reading task
      if (tasks.value[taskIndex.value].type == "reading") {
        result.value = {
          item: null,
          correct: true,
          createdAt: Timestamp.now(),
          difficulty: 3,
        };
      }

      // add results to task
      if (tasks.value[taskIndex.value].results) {
        tasks.value[taskIndex.value].results.push(result.value);
      } else {
        tasks.value[taskIndex.value].results = [result.value];
      }

      // Save result and move to next task
      try {
        const taskRef = doc(db, "tasks", tasks.value[taskIndex.value].id);
        await setDoc(taskRef, tasks.value[taskIndex.value], { merge: true });

        // reset results for next task
        result.value = null;

        // move to next tasks
        if (taskIndex.value <= tasks.value.length) {
          taskIndex.value++;
        }
      } catch (e) {
        console.error("Error adding document: ", e);
      }

      // Review completed
      if (taskIndex.value === tasks.value.length) {
        try {
          const userRef = doc(db, "users", user.value.uid);
          await updateDoc(userRef, {
            "reviews.completed": increment(1),
            "reviews.streak": sameDay(review.value.due.seconds, Timestamp.now().seconds) ? increment(1) : 0,
          });

          const reviewRef = doc(db, "users", user.value.uid, "reviews", review.value.id);
          await updateDoc(reviewRef, { completedAt: Timestamp.now() });

          // redirect user to /progress when last task is completed
          // router.push(`/progress?reviewComplete=${review.value.id}`);
          reviewComplete.value = true;
        } catch (e) {
          console.error("Error adding document: ", e);
        }
      }

      if (event) event.target.disabled = false;
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    function userSubmittedAnswer(value) {
      result.value = {
        item: value.item,
        correct: value.correct,
        createdAt: Timestamp.now(),
        difficulty: 3, //default value
      };

      showNextBtn.value = true;

      setTimeout(function () {
        document.getElementById("card-container").scrollIntoView({ behavior: "smooth", block: "end" });
      }, 100);
    }

    function skipTask(event) {
      if (event == "defer") {
        result.value = {
          defer: {
            days: 1,
          },
          createdAt: Timestamp.now(),
        };
      } else if (event == "delete") {
        result.value = {
          defer: {
            days: null,
          },
          createdAt: Timestamp.now(),
        };
      }

      // move to next task
      next();
    }

    return {
      skipTask,
      user,
      urlHostname,
      shuffleArray,
      router,
      tasks,
      userLoaded,
      taskIndex,
      showNextBtn,
      result,
      difficultyFeedback,
      next,
      userSubmittedAnswer,
      tasksLoaded,
      review,
      reviewComplete,
      reviewConstructionInProgress,
      taskSources,
      userIsNew,
      showSubscriptionPrompt,
      trialDaysLeft,
    };
  },
};
</script>
